import {
    Button,
    ButtonGroup,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    Grid,
    Switch,
    TextField,
} from "@mui/material";
import moment from "moment";
import * as React from "react";
import { OverageType } from "../../__generated__/globalTypes";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import DialogFooterWithLoading from "../Loading/DialogFooterWithLoading";
import styled from "styled-components";
import { Check } from "@mui/icons-material";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";
import {
    CreateContractForOrganizationMutation,
    CreateContractForOrganizationMutationVariables,
} from "./__generated__/CreateContractForOrganizationMutation";
import { GET_FULL_BILLING_DETAILS } from "../BillingDetails/BillingDetailsCardContent";
import { GetFullBillingDetails_organization } from "../BillingDetails/__generated__/GetFullBillingDetails";
import { GET_ORGANIZATIONS_QUERY } from "../OrganizationsOverview/OrganizationsOverview";
import { DatePicker } from "@mui/x-date-pickers";

export const PRICE_PER_MINUTE_TRANSCODED = 0.07;
export const PRICE_PER_MINUTE_VIEWED = 0.0015;
export const PRICE_PER_DRM_MINUTE_VIEWED = 0.0005;

export const CREATE_CONTRACT_FOR_ORGANIZATION_MUTATION = gql`
    mutation CreateContractForOrganizationMutation($input: CreateContractInput!) {
        createContractForOrganization(input: $input) {
            organizationId
        }
    }
`;

export const UPDATE_CONTRACT_FOR_ORGANIZATION_MUTATION = gql`
    mutation UpdateContractForOrganizationMutation($input: UpdateContractInput!) {
        updateContractForOrganization(input: $input) {
            organizationId
        }
    }
`;

interface Props {
    organization: GetFullBillingDetails_organization;
    defaultValues?: InputValues;
    onClose: () => void;
    id?: string;
}

interface InputValues {
    startDate: moment.Moment;
    endDate: moment.Moment;
    overageType: OverageType;
    billInGb: boolean;
    gbUsedPerMinute: number;
    hasAccessToSD: boolean;
}

// const CurrentPrice = styled.div`
//     margin-top: 5px;
//     color: ${(props) => props.theme.gray};
//     font-size: 11px;
// `;

const SwitchTitle = styled.span`
    font-size: 15px;
`;

function CreateCommitmentContract({ organization, onClose, defaultValues }: Props) {
    const [inputValues, setInputValues] = React.useState<InputValues>({
        startDate: defaultValues?.startDate ?? moment().utc().startOf("day"),
        endDate: defaultValues?.endDate ?? moment().utc().add(1, "year").startOf("day"),
        overageType: defaultValues?.overageType ?? OverageType.yearly,
        billInGb: defaultValues?.billInGb === true,
        gbUsedPerMinute: defaultValues?.gbUsedPerMinute ?? 0,
        hasAccessToSD: defaultValues?.hasAccessToSD === true,
    });

    const { organizationId } = organization;

    const { startDate, endDate, overageType, billInGb, hasAccessToSD, gbUsedPerMinute } = inputValues;

    const [createContractMut, { loading }] = useMutation<
        CreateContractForOrganizationMutation,
        CreateContractForOrganizationMutationVariables
    >(CREATE_CONTRACT_FOR_ORGANIZATION_MUTATION);

    function isValidInput() {
        if (moment(endDate).isBefore(moment(startDate))) {
            return false;
        }

        return true;
    }

    async function onCreate() {
        if (!isValidInput()) {
            toast.error("Invalid input", TOAST_SETTINGS);
        }
        try {
            await createContractMut({
                variables: {
                    input: {
                        organizationId,
                        start: startDate.utc().startOf("day").toISOString(),
                        end: endDate.utc().startOf("day").toISOString(),
                        overageType,
                        billInGb,
                        hasAccessToSD,
                        gbUsedPerMinute,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_FULL_BILLING_DETAILS,
                        variables: {
                            organizationId,
                        },
                    },
                    {
                        query: GET_ORGANIZATIONS_QUERY,
                    },
                ],
            });
            toast.success("New contract created");
            onClose();
        } catch (_e) {
            toast.error("Something went wrong 😞", TOAST_SETTINGS);
        }
    }

    return (
        <>
            <DialogContent style={{ overflowY: "hidden", marginTop: "20px" }}>
                <Grid container spacing={2}>
                    {/* {!billingDetails && (billAutomatically || billOveragesAutomatically) && (
                        <Grid item xs={12}>
                            <Alert severity="warning">
                                This organization has no billing details filled in. You can't assign a contract right
                                now. You can fill in the billing details{" "}
                                <HESPlink link={`/app/${organizationId}/billing`}>here</HESPlink>.
                            </Alert>
                        </Grid>
                    )} */}

                    <Grid item xs={12} md={6}>
                        <FormControl>
                            <HESPFormLabel label="Start date" />
                            <DatePicker
                                openTo="day"
                                format="YYYY/MM/DD"
                                views={["day"]}
                                value={startDate}
                                onChange={(newValue: moment.Moment | null) => {
                                    if (newValue !== null) {
                                        setInputValues({
                                            ...inputValues,
                                            startDate: newValue,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl>
                            <HESPFormLabel label="End date" />
                            <DatePicker
                                openTo="day"
                                format="YYYY/MM/DD"
                                views={["day"]}
                                minDate={startDate.add(1, "day")}
                                value={endDate}
                                onChange={(newValue: moment.Moment | null) => {
                                    if (newValue !== null) {
                                        setInputValues({
                                            ...inputValues,
                                            endDate: newValue,
                                        });
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <HESPFormLabel label="Overage calculation" />
                            <ButtonGroup color="secondary">
                                <Button
                                    variant={overageType === OverageType.monthly ? "contained" : "outlined"}
                                    onClick={(_e) =>
                                        setInputValues({
                                            ...inputValues,
                                            overageType: OverageType.monthly,
                                        })
                                    }
                                    startIcon={
                                        overageType === OverageType.monthly && <Check style={{ color: "white" }} />
                                    }
                                >
                                    Month
                                </Button>
                                <Button
                                    variant={overageType === OverageType.yearly ? "contained" : "outlined"}
                                    onClick={(_e) =>
                                        setInputValues({
                                            ...inputValues,
                                            overageType: OverageType.yearly,
                                        })
                                    }
                                    startIcon={
                                        overageType === OverageType.yearly && <Check style={{ color: "white" }} />
                                    }
                                >
                                    Annual
                                </Button>
                            </ButtonGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <HESPFormLabel label="Invoice calculation in GB used" />
                        <FormControl style={{ display: "flex", flexDirection: "row" }}>
                            <Switch
                                color="primary"
                                size="small"
                                checked={billInGb}
                                onChange={() =>
                                    setInputValues({
                                        ...inputValues,
                                        billInGb: !billInGb,
                                    })
                                }
                            />{" "}
                            <SwitchTitle>Convert minutes to GB</SwitchTitle>
                        </FormControl>
                    </Grid>
                    {billInGb && (
                        <Grid item xs={6}>
                            <HESPFormLabel label="GB per minute used" description="Conversion at 1 Mbit per second" />
                            <FormControl fullWidth>
                                <TextField
                                    type="number"
                                    value={gbUsedPerMinute}
                                    onChange={(e) =>
                                        setInputValues({
                                            ...inputValues,
                                            gbUsedPerMinute: parseFloat(e.target.value),
                                        })
                                    }
                                    fullWidth
                                    InputProps={{ inputProps: { min: 0.0, max: 10.0, step: 0.00001 } }}
                                />
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <HESPFormLabel label="Access to Service Desk" />
                        <FormControl style={{ display: "flex", flexDirection: "row" }}>
                            <Switch
                                color="primary"
                                size="small"
                                checked={hasAccessToSD}
                                onChange={() =>
                                    setInputValues({
                                        ...inputValues,
                                        hasAccessToSD: !hasAccessToSD,
                                    })
                                }
                            />{" "}
                            <SwitchTitle>Organization has access to Service Desk</SwitchTitle>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <DialogFooterWithLoading
                    isLoading={loading}
                    disabled={!isValidInput()}
                    onCancelClick={onClose}
                    defaultText={"Create"}
                    loadingText={"Creating..."}
                    onActionClick={onCreate}
                />
            </DialogActions>
        </>
    );
}

export default CreateCommitmentContract;
